/* eslint-disable */
import React, { useState, useEffect, useCallback } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";

/**
 * Sprint 13 — minimal admin surface for tournament CRUD.
 * Deliberately compact, deliberately not fancy. The intent is to
 * give an admin enough operational handles to run the catalog
 * without rebuilding the larger admin system.
 *
 * Auth: bearer token saved to localStorage("arc_admin_token").
 */
const TOKEN_KEY = "arc_admin_token";
const STATUSES = ["draft", "scheduled", "live", "settled", "archived"];
const VISIBILITIES = ["public", "private"];
const CATEGORIES = ["open", "vip", "arc-only", "creator"];
const TIERS = ["spectator", "challenger", "elite", "master"];

const blankForm = {
  id: "",
  title: "",
  category: "open",
  game: "",
  format: "",
  entry_fee_arc: 0,
  prize_pool_arc: 0,
  required_min_arc: 0,
  required_tier: "spectator",
  current_players: 0,
  max_players: 8,
  starts_at: "",
  status: "scheduled",
  visibility: "public",
};

async function authedFetch(path, token, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status} ${text || res.statusText}`);
  }
  return res.json();
}

export default function AdminTournaments() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ status: "", visibility: "", category: "" });
  const [editing, setEditing] = useState(null); // tournament id or "new"
  const [form, setForm] = useState(blankForm);

  const refresh = useCallback(async (tk) => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ limit: 100 });
      for (const [k, v] of Object.entries(filter)) {
        if (v) q.set(k, v);
      }
      const list = await authedFetch(`/admin/tournaments?${q}`, tk);
      setRows(list.rows);
      const s = await authedFetch(`/admin/tournaments/stats`, tk);
      setStats(s);
    } catch (e) {
      toast.error(`load failed: ${e.message}`);
      if (String(e.message).startsWith("401") || String(e.message).startsWith("403")) {
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
      }
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (authed && token) refresh(token);
  }, [authed, token, refresh]);

  const onLogin = async () => {
    if (!token) {
      toast.error("token required");
      return;
    }
    // Sprint 13 UX fix: probe stats with the candidate token BEFORE flipping
    // authed=true, so wrong-token users stay on the login screen with an
    // inline error rather than briefly flashing the empty admin shell.
    try {
      await authedFetch(`/admin/tournaments/stats`, token);
    } catch (e) {
      toast.error(`auth failed: ${e.message}`);
      return;
    }
    localStorage.setItem(TOKEN_KEY, token);
    setAuthed(true);
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setRows([]);
    setStats(null);
  };

  const onEdit = (t) => {
    setEditing(t.id);
    setForm({
      id: t.id,
      title: t.title,
      category: t.category,
      game: t.game,
      format: t.format,
      entry_fee_arc: t.entry_fee_arc,
      prize_pool_arc: t.prize_pool_arc,
      required_min_arc: t.required_min_arc,
      required_tier: t.required_tier,
      current_players: t.current_players,
      max_players: t.max_players,
      starts_at: (t.starts_at || "").slice(0, 16),
      status: t.status,
      visibility: t.visibility,
    });
  };

  const onNew = () => {
    setEditing("new");
    setForm({
      ...blankForm,
      starts_at: new Date(Date.now() + 86400_000).toISOString().slice(0, 16),
    });
  };

  const onCancel = () => {
    setEditing(null);
    setForm(blankForm);
  };

  const onSave = async () => {
    try {
      const payload = {
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
      };
      if (editing === "new") {
        if (!payload.id) delete payload.id;
        await authedFetch(`/admin/tournaments`, token, { method: "POST", body: JSON.stringify(payload) });
        toast.success("created");
      } else {
        // remove id from PUT payload (immutable) and remove the static fields the API doesn't expect
        const { id, ...rest } = payload;
        await authedFetch(`/admin/tournaments/${editing}`, token, { method: "PUT", body: JSON.stringify(rest) });
        toast.success("updated");
      }
      onCancel();
      refresh(token);
    } catch (e) {
      toast.error(`save failed: ${e.message}`);
    }
  };

  const action = async (id, path, label) => {
    try {
      await authedFetch(`/admin/tournaments/${id}${path}`, token, { method: "POST" });
      toast.success(`${label} ok`);
      refresh(token);
    } catch (e) {
      toast.error(`${label} failed: ${e.message}`);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0a0b10] text-white flex items-center justify-center p-6">
        <div className="w-full max-w-md p-8 border border-cyan-400/30 bg-[#0d0f17] rounded-md">
          <h1 className="text-xl font-mono mb-4 text-cyan-300" data-testid="admin-login-title">
            ARC Admin · Tournaments
          </h1>
          <p className="text-sm text-white/60 mb-4">
            Bearer token required. Set <code className="text-cyan-300">ARC_ADMIN_TOKEN</code> in <code>backend/.env</code>.
          </p>
          <input
            data-testid="admin-token-input"
            type="password"
            placeholder="paste admin bearer token"
            className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 mb-3 font-mono text-sm"
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
          <button
            data-testid="admin-login-btn"
            onClick={onLogin}
            className="w-full bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-2 rounded font-mono"
          >
            authenticate
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0b10] text-white p-6 font-mono" data-testid="admin-tournaments-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl text-cyan-300">ARC Admin · Tournaments</h1>
          <div className="flex gap-3">
            <button
              data-testid="admin-new-btn"
              onClick={onNew}
              className="bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-2 rounded text-sm"
            >
              + new tournament
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded text-sm"
            >
              logout
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 text-sm">
            <div className="border border-white/10 bg-[#0d0f17] p-3 rounded">
              <div className="text-white/50 text-xs">total</div>
              <div className="text-cyan-300 text-xl" data-testid="admin-stat-total">{stats.total}</div>
            </div>
            {Object.entries(stats.by_status).map(([k, v]) => (
              <div key={k} className="border border-white/10 bg-[#0d0f17] p-3 rounded">
                <div className="text-white/50 text-xs">status: {k}</div>
                <div className="text-cyan-300 text-xl">{v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4 text-sm">
          {["status", "visibility", "category"].map((f) => (
            <select
              key={f}
              data-testid={`admin-filter-${f}`}
              className="bg-black/40 border border-white/10 rounded px-2 py-1"
              value={filter[f]}
              onChange={(e) => setFilter({ ...filter, [f]: e.target.value })}
            >
              <option value="">all {f}</option>
              {(f === "status" ? STATUSES : f === "visibility" ? VISIBILITIES : CATEGORIES).map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          ))}
        </div>

        {/* Edit form */}
        {editing && (
          <div className="border border-cyan-400/30 bg-[#0d0f17] p-4 rounded mb-6" data-testid="admin-edit-form">
            <div className="text-cyan-300 text-sm mb-3">
              {editing === "new" ? "new tournament" : `edit · ${editing}`}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {editing === "new" && (
                <Input label="id (optional, auto-slug)" testid="form-id" value={form.id}
                       onChange={(v) => setForm({ ...form, id: v })} />
              )}
              <Input label="title" testid="form-title" value={form.title}
                     onChange={(v) => setForm({ ...form, title: v })} />
              <Select label="category" testid="form-category" options={CATEGORIES}
                      value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
              <Input label="game" testid="form-game" value={form.game}
                     onChange={(v) => setForm({ ...form, game: v })} />
              <Input label="format" testid="form-format" value={form.format}
                     onChange={(v) => setForm({ ...form, format: v })} />
              <Input label="entry_fee_arc" testid="form-entry" type="number" value={form.entry_fee_arc}
                     onChange={(v) => setForm({ ...form, entry_fee_arc: parseFloat(v || 0) })} />
              <Input label="prize_pool_arc" testid="form-prize" type="number" value={form.prize_pool_arc}
                     onChange={(v) => setForm({ ...form, prize_pool_arc: parseFloat(v || 0) })} />
              <Input label="required_min_arc" testid="form-minarc" type="number" value={form.required_min_arc}
                     onChange={(v) => setForm({ ...form, required_min_arc: parseFloat(v || 0) })} />
              <Select label="required_tier" testid="form-tier" options={TIERS}
                      value={form.required_tier} onChange={(v) => setForm({ ...form, required_tier: v })} />
              <Input label="current_players" testid="form-current" type="number" value={form.current_players}
                     onChange={(v) => setForm({ ...form, current_players: parseInt(v || 0, 10) })} />
              <Input label="max_players" testid="form-max" type="number" value={form.max_players}
                     onChange={(v) => setForm({ ...form, max_players: parseInt(v || 0, 10) })} />
              <Input label="starts_at" testid="form-starts" type="datetime-local" value={form.starts_at}
                     onChange={(v) => setForm({ ...form, starts_at: v })} />
              <Select label="status" testid="form-status" options={STATUSES}
                      value={form.status} onChange={(v) => setForm({ ...form, status: v })} />
              <Select label="visibility" testid="form-visibility" options={VISIBILITIES}
                      value={form.visibility} onChange={(v) => setForm({ ...form, visibility: v })} />
            </div>
            <div className="flex gap-2 mt-4">
              <button data-testid="form-save-btn" onClick={onSave}
                      className="bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-2 rounded text-sm">save</button>
              <button data-testid="form-cancel-btn" onClick={onCancel}
                      className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded text-sm">cancel</button>
            </div>
          </div>
        )}

        {/* Tournament list */}
        <div className="border border-white/10 bg-[#0d0f17] rounded overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-tournaments-table">
            <thead className="bg-black/40 text-white/60">
              <tr>
                <th className="text-left p-3">id</th>
                <th className="text-left p-3">title</th>
                <th className="text-left p-3">category</th>
                <th className="text-left p-3">status</th>
                <th className="text-left p-3">visibility</th>
                <th className="text-left p-3">actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="p-3 text-white/50">loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="p-3 text-white/50">no tournaments match filters</td></tr>
              )}
              {!loading && rows.map((t) => (
                <tr key={t.id} className="border-t border-white/5" data-testid={`admin-row-${t.id}`}>
                  <td className="p-3 text-cyan-300/80">{t.id}</td>
                  <td className="p-3">{t.title}</td>
                  <td className="p-3">{t.category}</td>
                  <td className="p-3">{t.status}</td>
                  <td className="p-3">{t.visibility}</td>
                  <td className="p-3 flex gap-2 flex-wrap">
                    <button data-testid={`admin-edit-${t.id}`} onClick={() => onEdit(t)}
                            className="text-xs bg-white/10 hover:bg-white/20 px-2 py-1 rounded">edit</button>
                    {t.visibility === "public" ? (
                      <button data-testid={`admin-unpublish-${t.id}`}
                              onClick={() => action(t.id, "/unpublish", "unpublish")}
                              className="text-xs bg-white/10 hover:bg-white/20 px-2 py-1 rounded">unpublish</button>
                    ) : (
                      <button data-testid={`admin-publish-${t.id}`}
                              onClick={() => action(t.id, "/publish", "publish")}
                              className="text-xs bg-white/10 hover:bg-white/20 px-2 py-1 rounded">publish</button>
                    )}
                    {t.status !== "archived" && (
                      <button data-testid={`admin-archive-${t.id}`}
                              onClick={() => action(t.id, "/archive", "archive")}
                              className="text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 px-2 py-1 rounded">archive</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text" }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-white/50 text-xs">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-black/40 border border-white/10 rounded px-2 py-1"
      />
    </label>
  );
}

function Select({ label, value, options, onChange, testid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-white/50 text-xs">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-black/40 border border-white/10 rounded px-2 py-1"
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
